/* istanbul ignore file */
import type { CoreOptionalOperationType } from './types/configuration';

export const SIGN_OUT_PATH = '/sign-out';

export const enum EntityStatusValue {
  Published = 'published',
  Archived = 'archived',
  Draft = 'draft',
  Default = 'default',
  // Used to keep track of new entities without a BE state
  New = 'new',
}

export const DEFAULT_ENTITY_LIST_STATUSES = [
  EntityStatusValue.Draft,
  EntityStatusValue.Published,
] as const;

export const entityMetadataActions = [
  'publish',
  'unpublish',
  'clone',
  'update',
  'create',
  'archive',
  'unarchive',
] as const;

export enum ConfigEntityAuthAction {
  Get = 'get',
  Create = 'create',
  Update = 'update',
  Publish = 'publish',
  Unpublish = 'unpublish',
  Archive = 'archive',
  Unarchive = 'unarchive',
  Clone = 'clone',
  Validate = 'validate',
  Delete = 'delete',
}

export const operationToConfigEntityAuthAction: Record<
  CoreOptionalOperationType,
  ConfigEntityAuthAction
> = {
  create: ConfigEntityAuthAction.Create,
  update: ConfigEntityAuthAction.Update,
  archive: ConfigEntityAuthAction.Archive,
  publish: ConfigEntityAuthAction.Publish,
  republish: ConfigEntityAuthAction.Publish,
  unpublish: ConfigEntityAuthAction.Unpublish,
  unarchive: ConfigEntityAuthAction.Unarchive,
  clone: ConfigEntityAuthAction.Clone,
  deepClone: ConfigEntityAuthAction.Clone,
  copyToEnvironment: ConfigEntityAuthAction.Create,
  validate: ConfigEntityAuthAction.Validate,
};

export enum EntityOperationStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived',
}

export const entityDetailActionTypes = [
  'custom',
  'create',
  'update',
  'publish',
  'republish',
  'unpublish',
  'archive',
  'unarchive',
  'clone',
  'deepClone',
  'validate',
  'exitAndDiscardChanges',
] as const;

// There's some places in code that could be updated to use OperationMethod instead
// of using hard-coded strings
// https://everlong.atlassian.net/browse/CACT-1159

export enum OperationMethod {
  GET = 'GET',
  POST = 'POST',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}
/**
 * Category types for Domains and Entities - 'hidden' will hide the domain and entity from the navigation menu and entities with 'hidden' will not have a list page.
 * 'local-server' is for development-only fixtures (e.g. _example-entity) that target a local
 * server and are exempt from production contract checks. `local-server` entities are also
 * treated as non-CACT-BE at runtime (`isUsingConfigAppBackend: false`), since they ship their
 * own endpoints and don't speak the config-entities envelope/auth contract; see
 * `buildEntityConfigs` in apps/config-app/src/utils/create-tenant-config.ts.
 */
export enum Category {
  Default = 'default',
  Hidden = 'hidden',
  Core = 'core',
  LocalServer = 'local-server',
}

export const CustomOperationType = 'custom';

export const requiredOperations = ['list', 'get'] as const;

export const optionalOperations = [
  'create',
  'update',
  'archive',
  'publish',
  'republish',
  'unpublish',
  'unarchive',
  'clone',
  'deepClone',
  'copyToEnvironment',
  'custom',
  'validate',
] as const;

export const operations = [
  ...requiredOperations,
  ...optionalOperations,
] as const;

export const bulkActionOperations = [
  'export',
  'import',
  'copyToEnvironment',
  'clone',
  'publish',
  'archive',
  'delete',
] as const;

export type BulkActionOperationType = (typeof bulkActionOperations)[number];

export const entityDetailActions = [
  ...operations,
  'syncToEnvironment',
] as const;

export enum RulesTemplateType {
  completionCriteria = 'completionCriteria',
  rewardCriteria = 'rewardCriteria',
  eligibilityCriteria = 'eligibilityCriteria',
  filterRule = 'filterRule',
  branchingRule = 'branchingRule',
  challengeRule = 'challengeRule',
  ruleCombination = 'ruleCombination',
  cohortRule = 'cohortRule',
}

/**
 * Different error types we will handle in the config app
 * all 500 api errors are grouped to 'server-error-response'
 * all 400 api errors are grouped to 'domain-entity-error-response'
 * form validation (local) errors
 */
export enum ConfigErrorType {
  ServerError = 'server-error-response',
  DomainEntityError = 'domain-entity-error-response',
  FormValidationError = 'form-validation-error',
}
export const entityDetailActionPendingKey: Partial<
  Record<(typeof entityDetailActions)[number], string>
> = {
  create: 'SAVING_ACTION',
  update: 'SAVING_ACTION',
  publish: 'PUBLISHING_ACTION',
  republish: 'REPUBLISHING_ACTION',
  unpublish: 'UNPUBLISHING_ACTION',
  archive: 'ARCHIVING_ACTION',
  unarchive: 'UNARCHIVING_ACTION',
  clone: 'CLONE_ACTION',
  copyToEnvironment: 'COPYING_TO_ENVIRONMENT_ACTION',
  deepClone: 'DEEP_CLONING_ACTION',
  syncToEnvironment: 'SYNC_TO_ENVIRONMENT_ACTION',
  validate: 'VALIDATING_ACTION',
};

export const AuthoringEnvironmentValues = ['base', 'preview'] as const;

/**
 * Supported locale configuration
 * Add new locales here as needed - TypeScript will automatically update the union type
 */
export const Locales = [
  // English variants
  'en-US',
  'en-CA',
  'en-GB',
  'en-AU',
  'en-NZ',
  'en-IE',
  'en-ZA',
  // French variants
  'fr-FR',
  'fr-CA',
  'fr-BE',
  'fr-CH',
  // Spanish variants
  'es-ES',
  'es-MX',
  'es-AR',
  'es-CO',
  'es-CL',
  'es-PE',
  'es-VE',
  // German variants
  'de-DE',
  'de-AT',
  'de-CH',
  // Italian variants
  'it-IT',
  'it-CH',
  // Portuguese variants
  'pt-PT',
  'pt-BR',
  // Dutch variants
  'nl-NL',
  'nl-BE',
  // Chinese variants
  'zh-CN',
  'zh-TW',
  'zh-HK',
  // Japanese, Korean, Russian
  'ja-JP',
  'ko-KR',
  'ru-RU',
  // Arabic variants
  'ar-SA',
  'ar-EG',
  'ar-AE',
  // Nordic & Eastern European
  'sv-SE',
  'da-DK',
  'no-NO',
  'fi-FI',
  'pl-PL',
  'cs-CZ',
  'hu-HU',
  // Other
  'tr-TR',
  'he-IL',
  'th-TH',
  'vi-VN',
  'id-ID',
  'ms-MY',
  'hi-IN',
] as const;

// Central source of truth for URL protocol policy.
// Default allow-list for protocols. Only these (and relative URLs) are permitted.
// Note: entries here must omit the trailing ':'; normalization will add it.
export const DEFAULT_ALLOWED_URI_PROTOCOLS = [
  'https',
  'ftps',
  'xmpp',
  'tel',
  'mailto',
  'callto',
  'sms',
];

export const ASSET_TYPES = ['image', 'pdf'] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ENTITY_TYPE_TO_ASSET_TYPE: Record<string, AssetType> = {
  'config-image': 'image',
  'config-pdf': 'pdf',
};

export const ASSET_TYPE_TO_ENTITY_TYPE = Object.fromEntries(
  Object.entries(ENTITY_TYPE_TO_ASSET_TYPE).map(([entityType, assetType]) => [
    assetType,
    entityType,
  ]),
) as Record<AssetType, string>;

export const URI_FORMATS = ['uri', 'uri-reference', 'uri-template'];

export const CODE_EDITOR_LANGUAGES = ['yaml', 'json', 'plaintext'] as const;
export type CodeEditorLanguage = (typeof CODE_EDITOR_LANGUAGES)[number];
